import { css } from "@emotion/react";
import { useTranslations } from "next-intl";
import { ReactNode } from "react";

import { Icon } from "@zapier/zinnia-icons";
import {
  Button,
  Colors,
  Link,
  Surface,
  Text,
  Typography,
} from "@zapier/design-system";

import { SizeType } from "@zapier/reporting/src/hooks";

const title = css`
  ${Typography.Body4}
  color: ${Colors.GrayWarm10};
  font-weight: 600;
`;

const wrapper = css`
  margin: auto;
`;

const actionButtonWrapper = css`
  margin-top: 20px;
  width: 100%;
`;

const buttonWrapper = css`
  width: 175px;
`;

const createZapLinkStyle = css`
  width: 100%;
`;

const textWrapper = css`
  align-items: center;
  display: grid;
  grid-gap: 5px;
  justify-content: center;
  justify-items: center;
  padding: 35px;
`;

const surfaceWrapper = css`
  display: flex;
  width: 550px;
`;

const surfaceWrapperSmall = css`
  width: auto;
`;

type Props = {
  handleFilterReset?: () => void;
  hasError?: boolean;
  isFilterEmpty: boolean;
  isOutOfRetentionPeriod?: boolean;
  size: SizeType;
};

function Content(props: Props) {
  const t = useTranslations("no-runs-surface");

  if (props.hasError) {
    const message = props.isFilterEmpty
      ? "This error has been reported, and our team will look into it shortly."
      : "Adjust your filters and try again.";

    const buttonAction = props.isFilterEmpty
      ? () => document.location.reload()
      : props.handleFilterReset;

    return (
      <div css={textWrapper}>
        <Icon color="UiPrimaryStronger" name="alertTriangle" size={25} />
        <Text type="Body4">{t("error-title")}</Text>
        <Text color="GrayWarm10" textAlign="center" type="SmallPrint1">
          {t.rich("error-contact", {
            message,
            link: (chunks: ReactNode) => (
              <Link href="https://zapier.com/app/get-help">{chunks}</Link>
            ),
          })}
        </Text>
        <div css={actionButtonWrapper}>
          <Button isFullWidth={true} onPress={buttonAction}>
            {t("empty-action", {
              isFilterEmpty: props.isFilterEmpty ? "true" : "false",
            })}
          </Button>
        </div>
      </div>
    );
  }

  if (props.isFilterEmpty) {
    return (
      <div css={textWrapper}>
        <Icon color="UiPrimaryStronger" name="miscClock" size={25} />
        <p css={title}>{t("no-runs-title")}</p>
        <span css={buttonWrapper}>
          <Link
            css={createZapLinkStyle}
            href="/webintent/create-zap"
            variant="primary"
          >
            {t("create-zap")}
          </Link>
        </span>
        <Text type="Body1">
          {t("or-separator")}{" "}
          <Link href="/explore">{t("explore-templates")}</Link>
        </Text>
      </div>
    );
  }

  return (
    <div css={textWrapper}>
      <Icon color="UiPrimaryStronger" name="navSearch" size={25} />
      <p css={title}>{t("no-results-title")}</p>
      {props.isOutOfRetentionPeriod && (
        <Text color="GrayWarm10" type="SmallPrint1">
          {t("retention-notice")}{" "}
        </Text>
      )}
      <Text color="GrayWarm10" type="SmallPrint1">
        {t("adjust-filters")}
      </Text>
      <div css={actionButtonWrapper}>
        <Button isFullWidth={true} onPress={props.handleFilterReset}>
          {t("clear-filters")}
        </Button>
      </div>
    </div>
  );
}

export function NoRunsSurface(props: Props) {
  return (
    <div css={wrapper}>
      <span
        css={[surfaceWrapper, props.size === "small" && surfaceWrapperSmall]}
      >
        <Surface backgroundColor="GrayWarm2">
          <Content {...props} />
        </Surface>
      </span>
    </div>
  );
}
